import { useCallback, useEffect, useRef, useState } from 'react';
import { getHidePref, setHidePref, migrateHidePrefFromCookie } from '../utils/addToHomePref';
import { isIosSafari, isStandalone } from '../utils/addToHomeDetection';

const DISPLAY_DELAY_MS = 2000;

export interface UseAddToHomePromptResult {
  isOpen: boolean;
  close: (rememberHide: boolean) => void;
}

/**
 * iOS Safari で fingering ページを開いた際に、ホーム画面追加の手順案内モーダルを
 * `ready=true` を契機に 2 秒遅延で表示するかどうかを判定する hook。
 *
 * 判定順:
 *  1. ready=false なら何もしない (PDF 未生成等)
 *  2. 一度発火済み (triggeredRef) なら何もしない (同セッション再表示防止)
 *  3. standalone モードなら何もしない
 *  4. iOS Safari 以外なら何もしない
 *  5. cookie が「今後表示しない」なら何もしない
 *  6. 上記をすべて通過 → 2 秒後に isOpen=true
 */
export function useAddToHomePrompt(ready: boolean): UseAddToHomePromptResult {
  const [isOpen, setIsOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggeredRef = useRef(false);

  useEffect(() => {
    migrateHidePrefFromCookie();
  }, []);

  useEffect(() => {
    if (!ready) return;
    if (triggeredRef.current) return;
    if (isStandalone()) return;
    if (!isIosSafari(navigator.userAgent)) return;
    if (getHidePref()) return;

    // setTimeout の直前に同期セットして、StrictMode の二重マウントや ready の
    // 連続変化で多重起動するのを防ぐ。タイマー callback 内ではない (spec §6)。
    triggeredRef.current = true;
    timerRef.current = setTimeout(() => {
      setIsOpen(true);
      timerRef.current = null;
    }, DISPLAY_DELAY_MS);

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [ready]);

  const close = useCallback((rememberHide: boolean) => {
    if (rememberHide) setHidePref(true);
    setIsOpen(false);
  }, []);

  return { isOpen, close };
}
